import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { Box, List } from '@mui/material';

/* Project */
import { Menus } from 'config/menus';
import { falsy, formatCLP, getAuctionTypeName } from 'utils/functions';

import SideBarShell from './shared/SideBarShell';
import BalanceBox from './shared/BalanceBox';
import ProfileActions from './shared/ProfileActions';
import AuthActions from './shared/AuthActions';
import GoToLiveButton from './shared/GoToLiveButton';
import CategorySection from './shared/CategorySection';
import { useSideBarItems } from './shared/useSideBarItems';
import { useCollapsible } from './shared/useCollapsible';
import { SECTION, sectionsForUser } from './config/sections';

/**
 * SideBar data-driven: recibe (indirectamente, vía el dispatcher) un array de
 * secciones tipadas y las renderiza. La diferencia entre admin / cliente /
 * invitado vive en los datos (config/sections.js), no en este componente.
 */
function SideBar(props) {
  const {
    user,
    balance,
    actions,
    isOpen,
    toggleDrawer,
    globals,
    propertiesIsLive,
    furnituresIsLive,
    propertyBalance,
  } = props;

  const hasUser = !falsy(user);
  const isClient = user?.roleCode === 'CLI';

  const currentLive =
    propertiesIsLive && propertiesIsLive.AuctionType?.code !== 'BID'
      ? propertiesIsLive
      : furnituresIsLive;
  const islive = !!currentLive;
  const externalRolLink = globals.rolExternalLink;
  const liveName = getAuctionTypeName(currentLive?.AuctionType?.code, currentLive?.areaId).live;

  const { mapView, mapPaths, externalLinks } = useSideBarItems({ user, actions, globals });
  const { isOpen: isSectionOpen, toggle: toggleSection } = useCollapsible();

  // Lista de secciones según el perfil del usuario.
  const sections = useMemo(() => sectionsForUser({ hasUser, isClient }), [hasUser, isClient]);

  const renderSection = (section, index) => {
    switch (section.type) {
      case SECTION.BALANCE:
        if (!hasUser || balance == null) return null;
        return (
          <Box key={`sec-${index}`} sx={{ px: 2, pt: 1.5 }}>
            <BalanceBox
              balance={balance}
              propertyBalance={propertyBalance}
              onRefresh={() => actions.fetchBalance()}
              formatCLP={formatCLP}
            />
          </Box>
        );

      case SECTION.GOTO_LIVE:
        if (!hasUser) return null;
        return (
          <Box key={`sec-${index}`} sx={{ px: 2, pt: 2, pb: 1 }}>
            <GoToLiveButton
              hasUser={hasUser}
              islive={islive}
              liveName={liveName}
              externalRolLink={externalRolLink}
            />
          </Box>
        );

      case SECTION.PROFILE_ACTIONS:
        if (!hasUser) return null;
        return <ProfileActions key={`sec-${index}`} mapView={mapView} actions={actions} />;

      case SECTION.AUTH_ACTIONS:
        if (hasUser) return null;
        return <AuthActions key={`sec-${index}`} mapView={mapView} />;

      case SECTION.FLAT: {
        const items = mapPaths(section.paths, `flat-${index}`);
        const extra = section.withExternal ? externalLinks : [];
        if (items.length === 0 && extra.length === 0) return null;
        return (
          <List key={`sec-${index}`} disablePadding sx={{ py: 0.5 }}>
            {items}
            {extra}
          </List>
        );
      }

      case SECTION.COLLAPSIBLE: {
        const items = mapPaths(section.paths, section.title, { sortAlpha: section.sortAlpha });
        // El "Menú" también lista los enlaces externos (Login Externo Bienes/Props).
        const extra = section.withExternal ? externalLinks : [];
        if (items.length === 0 && extra.length === 0) return null;
        return (
          <CategorySection
            key={`sec-${index}`}
            label={section.title}
            open={isSectionOpen(section.title)}
            onToggle={() => toggleSection(section.title)}>
            {items}
            {extra}
          </CategorySection>
        );
      }

      default:
        return null;
    }
  };

  return (
    <SideBarShell
      isOpen={isOpen}
      toggleDrawer={toggleDrawer}
      actions={actions}
      navBarProps={props}>
      {sections.map(renderSection)}
    </SideBarShell>
  );
}

SideBar.propTypes = {
  propertiesIsLive: PropTypes.object,
  furnituresIsLive: PropTypes.object,
};

export default SideBar;
